// useMediaRecorder.js
//
// Core, framework-light logic for the in-browser camera/mic recorder. Kept
// separate from the UI so the load-bearing parts (mime selection, the recorder
// lifecycle, the duration cap, blob -> File naming) are unit-testable without a
// DOM or a real camera. The React surface lives in MediaRecorderInner.jsx.
//
// Output container note: prefer MP4 (H.264/AAC) where the browser can record it
// — modern Chrome/Edge/Safari support MediaRecorder MP4, which the upload
// backend accepts directly (its presigned-URL allowlist is mp4/mov/avi/mkv and
// REJECTS webm with a 400 "Invalid file extension"). We fall back to webm only
// on browsers that cannot encode MP4 (e.g. Firefox); those still need the
// backend to accept/transcode webm (tracked follow-up). iOS Safari can neither
// record nor play webm and is handled by the unsupported-environment screen.
//
// Candidates are ordered best -> acceptable; the first the browser supports wins.

import { useCallback, useEffect, useRef, useState } from "react";

import { cameraConstraint, cameraErrorFor } from "./cameraDevices";

// MP4 first (real H.264/AAC the backend accepts), then webm as a fallback.
export const VIDEO_MIME_CANDIDATES = [
  "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
  "video/mp4",
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
];

export const AUDIO_MIME_CANDIDATES = [
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/webm;codecs=opus",
  "audio/webm",
];

// Default hard cap on a single recording so a user cannot accidentally upload a
// multi-hour blob. Callers override via the maxDurationMs prop.
export const DEFAULT_MAX_DURATION_MS = 5 * 60 * 1000; // 5 minutes

// Hard cap on the recorded blob's byte size, independent of the duration cap.
// A high-bitrate camera can produce a very large webm well inside 5 minutes;
// uploading it risks OOM on mobile or a silent upload failure. Guard before the
// blob is exposed for upload.
export const MAX_RECORDING_BYTES = 100 * 1024 * 1024; // 100 MB (video)

// Audio gets a MUCH smaller cap than video (QA-95): 100 MB of speech is hours
// long, far beyond anything the 5-minute duration cap can ever produce, so the
// number guarded nothing and just read as alarmingly large.
export const MAX_AUDIO_RECORDING_BYTES = 15 * 1024 * 1024; // 15 MB (audio)

/**
 * The byte cap that applies to a given recording mode. Audio gets a much
 * smaller cap than video (QA-95): 100 MB of speech is hours long, far beyond
 * anything the 5-minute duration cap can produce, so the number guarded nothing
 * and read as alarmingly large. Video keeps the 100 MB cap the upload path
 * guards with, so RECORD and UPLOAD never quote two different video limits.
 */
export const maxRecordingBytesForMode = (mode) =>
  mode === "audio" ? MAX_AUDIO_RECORDING_BYTES : MAX_RECORDING_BYTES;

// Ask MediaRecorder for a chunk every second instead of one blob at stop().
// Without a timeslice `ondataavailable` fires exactly once (at stop), so the
// live storage counter (QA-88) would read 0 MB for the whole recording and then
// jump to the final size — i.e. it could never be live. The chunks are the same
// bytes either way; only their delivery cadence changes.
export const RECORDING_TIMESLICE_MS = 1000;

// Fraction of the byte cap at which the live counter starts warning.
export const STORAGE_WARN_RATIO = 0.8;

/**
 * True once a recording is close enough to its byte cap that the UI should warn
 * (QA-88: "visually warn approaching the cap"). Pure so the threshold is
 * asserted without a recorder.
 */
export function isNearRecordingCap(bytes, maxBytes = MAX_RECORDING_BYTES) {
  return (
    typeof bytes === "number" &&
    bytes > 0 &&
    bytes >= maxBytes * STORAGE_WARN_RATIO
  );
}

/**
 * Bytes as a compact "12.4 MB" string for the live storage counter (QA-88).
 * One decimal place: enough to visibly move each second, not so much that the
 * number jitters distractingly.
 */
export function formatMegabytes(bytes) {
  const mb = (Number(bytes) || 0) / (1024 * 1024);
  return `${mb.toFixed(1)} MB`;
}

/**
 * A duration cap in PLAIN WORDS — "5 minutes", never "05:00" (QA-92, QA-95).
 *
 * Gordon, twice: a clock-formatted cap reads as a countdown that is already
 * running. Words read as a limit. The running elapsed timer stays clock-
 * formatted (it IS a clock); every CAP the user is told about uses this.
 */
export function formatDurationWords(ms) {
  const totalSeconds = Math.max(0, Math.round((Number(ms) || 0) / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const parts = [];
  if (minutes) parts.push(`${minutes} ${minutes === 1 ? "minute" : "minutes"}`);
  if (seconds || !minutes) {
    parts.push(`${seconds} ${seconds === 1 ? "second" : "seconds"}`);
  }
  return parts.join(" ");
}

/**
 * True if a recorded blob exceeds the upload size cap. Pure + injectable so the
 * guard is unit-testable without a real recording.
 * @param {Blob|{size:number}} blob
 * @param {number} [maxBytes]
 */
export function isRecordingTooLarge(blob, maxBytes = MAX_RECORDING_BYTES) {
  return !!blob && typeof blob.size === "number" && blob.size > maxBytes;
}

/**
 * Pick the first MediaRecorder mimeType the current browser supports.
 * @param {"video"|"audio"} mode
 * @param {typeof MediaRecorder} [Recorder] injectable for tests
 * @returns {string} a supported mime string, or "" to let the browser decide
 */
export function pickSupportedMimeType(mode, Recorder) {
  const R =
    Recorder ||
    (typeof MediaRecorder !== "undefined" ? MediaRecorder : undefined);
  const candidates =
    mode === "audio" ? AUDIO_MIME_CANDIDATES : VIDEO_MIME_CANDIDATES;
  if (!R || typeof R.isTypeSupported !== "function") {
    // Old browser: return the bare type and hope the UA can encode it.
    return candidates[candidates.length - 1];
  }
  for (const type of candidates) {
    if (R.isTypeSupported(type)) return type;
  }
  return "";
}

// Containers the upload backend currently accepts (its presigned-URL allowlist
// is mp4/mov/avi/mkv). webm is intentionally excluded here: MediaRecorder can
// emit webm, but the backend rejects it today with 400 "Invalid file
// extension", so a webm-only browser (e.g. Firefox) cannot complete the
// record -> upload flow. When the backend starts accepting/transcoding webm,
// add the webm types here and the Record option re-appears on those browsers.
export const UPLOADABLE_RECORDING_MIME = {
  video: ["video/mp4;codecs=avc1.42E01E,mp4a.40.2", "video/mp4"],
  audio: ["audio/mp4;codecs=mp4a.40.2", "audio/mp4"],
};

/**
 * Whether the CURRENT runtime can record a clip the upload backend will accept
 * for the given mode. Used to decide if the "Record" option should be offered
 * at all — so devices/browsers that can't complete the flow (no getUserMedia /
 * no MediaRecorder / can only encode webm, e.g. Firefox) never see a dead-end
 * Record tab and just get Upload instead. Injectables exist for tests.
 * @param {"video"|"audio"} mode
 * @param {{mediaDevices?: object, Recorder?: typeof MediaRecorder}} [deps]
 * @returns {boolean}
 */
export function canRecordMedia(mode = "video", { mediaDevices, Recorder } = {}) {
  const md =
    mediaDevices ||
    (typeof navigator !== "undefined" ? navigator.mediaDevices : undefined);
  const R =
    Recorder ||
    (typeof MediaRecorder !== "undefined" ? MediaRecorder : undefined);
  if (!md || typeof md.getUserMedia !== "function") return false;
  if (!R || typeof R.isTypeSupported !== "function") return false;
  const accepted =
    UPLOADABLE_RECORDING_MIME[mode === "audio" ? "audio" : "video"];
  return accepted.some((type) => R.isTypeSupported(type));
}

/**
 * The getUserMedia constraints for each mode. Mic-only for audio; camera+mic
 * for video.
 * @param {"video"|"audio"} mode
 */
export function getConstraints(mode, deviceId = null) {
  if (mode === "audio") {
    return { audio: true, video: false };
  }
  return {
    audio: true,
    // QA-105 — honour the camera the user picked. `cameraConstraint` pins a
    // chosen device with `exact` (a non-exact deviceId is only a hint the
    // browser may ignore) and falls back to the previous `facingMode: "user"`
    // when nothing is chosen, so the no-picker path is unchanged.
    video: cameraConstraint(deviceId),
  };
}

/**
 * Convert a recorded Blob into a File the existing upload pipeline accepts. The
 * filename extension is derived from the mime so slugifyFilename + the
 * extension-sniffing callers behave. webm -> .webm.
 * @param {Blob} blob
 * @param {"video"|"audio"} mode
 * @param {() => number} [now] injectable clock for tests
 */
export function blobToFile(blob, mode, now = Date.now) {
  const mime = blob.type || (mode === "audio" ? "audio/webm" : "video/webm");
  // "audio/webm;codecs=opus" -> "webm"
  const subtype = mime.split(";")[0].split("/")[1] || "webm";
  // NB: an audio recording captured into an MP4 container keeps the ".mp4"
  // extension on purpose. The standard ".m4a" extension would be cleaner, but
  // the upload backend's presigned-URL allowlist rejects ".m4a" (400 "Invalid
  // file extension"); it accepts ".mp4". Because of that the backend signs the
  // URL with content-type video/mp4 even for audio — handled at upload time by
  // sending back the backend's own content_type so the S3 signature matches
  // (see uploadFile in utils/s3Helper.js).
  const stamp = now();
  const name = `${mode === "audio" ? "recording-audio" : "recording-video"}-${stamp}.${subtype}`;
  return new File([blob], name, { type: mime.split(";")[0] });
}

/**
 * THE single source of truth for what the user is told when "Use this
 * recording" fails to upload.
 *
 * WHY THIS IS A SHARED PRIMITIVE (G-118): MediaRecorderInner has two "Use"
 * paths — the built-in wheel upload and the alternate `onUse` consumer (the
 * Capture composer). The wheel path grew a real toast; the `onUse` path was
 * written later and only `console.error`d, so a failed upload gave the user
 * ZERO feedback and they walked away believing the take was gone. That is the
 * second cause of the recording-loss Kelly filmed. Both paths now call this
 * one function, so the two can never drift apart again.
 *
 * Pure + injectable so every branch is unit-testable with no DOM and no camera.
 *
 * @param {object} args
 * @param {string} [args.fileName]  the recorded File's name (extension matters)
 * @param {any} [args.error]        whatever the upload rejected with
 * @param {boolean} [args.online]   navigator.onLine at failure time
 * @param {number} [args.maxBytes]  the byte cap that applied to this recording,
 *   so an audio clip is told its own 15 MB limit and not video's 100 MB.
 * @returns {string} a user-facing message that always states the recording is
 *   still there and retryable, EXCEPT where retrying provably cannot work.
 */
export function describeRecordingUploadFailure({
  fileName = "",
  error,
  online = true,
  maxBytes = MAX_RECORDING_BYTES,
} = {}) {
  const ext = String(fileName).split(".").pop().toLowerCase();
  const status =
    error?.response?.status ?? error?.status ?? error?.statusCode ?? null;
  const text = `${error?.response?.data?.message || ""} ${
    error?.message || ""
  }`.toLowerCase();

  // Offline / network drop. Retrying WILL work once they reconnect, and the
  // clip is still in memory — say both.
  if (online === false || text.includes("network error")) {
    return "You appear to be offline. Your recording is still here — reconnect and tap “Use this recording” again.";
  }

  // A webm file means the browser couldn't encode MP4 (e.g. Firefox) and the
  // upload backend rejects webm today, so "try again" would never succeed —
  // tell the user accurately instead of looping them on a dead retry.
  if (ext === "webm") {
    return "This browser saves recordings in a format we can’t upload yet. Please use Chrome or Edge, or upload a file instead.";
  }

  // Payload rejected for size. Retrying the same clip cannot succeed.
  if (status === 413 || text.includes("too large")) {
    const maxMb = Math.round(maxBytes / (1024 * 1024));
    return `This recording is too large to upload (${maxMb} MB max). Please record a shorter clip.`;
  }

  // Payload rejected for type/extension. Retrying the same clip cannot succeed.
  if (text.includes("invalid file extension") || text.includes("unsupported")) {
    return "We can’t upload this recording’s file type. Please upload a file instead.";
  }

  // Everything else (transient/server/mid-flight) IS retryable.
  return "Could not upload the recording. Your recording is still here — please tap “Use this recording” to try again.";
}

/**
 * React hook wrapping the MediaRecorder lifecycle. UI calls requestStream() to
 * prompt for permission, start()/stop() to record, and reset() to discard.
 *
 * State machine: idle -> requesting -> ready -> recording -> recorded
 *                 |__________________ denied / error (terminal until reset)
 *
 * Injectables (mediaDevices, Recorder) exist purely so the hook can be exercised
 * without a real camera in tests; in the app they default to the globals.
 */
export function useMediaRecorder({
  mode = "video",
  maxDurationMs = DEFAULT_MAX_DURATION_MS,
  mediaDevices,
  Recorder,
  // QA-105 — the camera the user picked, or null for the browser default. Video
  // mode only; an audio recording has no camera to choose.
  cameraDeviceId = null,
} = {}) {
  const [status, setStatus] = useState("idle"); // idle|requesting|ready|recording|recorded|denied|error
  const [errorMessage, setErrorMessage] = useState(null);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [elapsedMs, setElapsedMs] = useState(0);
  // Bytes accumulated so far, summed from the timesliced ondataavailable chunks
  // (QA-88). This is the SAME data that becomes the final blob, so the counter
  // is the real recording weight, not an estimate from bitrate × elapsed.
  const [recordedBytes, setRecordedBytes] = useState(0);
  const maxBytes = maxRecordingBytesForMode(mode);

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const tickRef = useRef(null);
  const capRef = useRef(null);

  const devices =
    mediaDevices ||
    (typeof navigator !== "undefined" ? navigator.mediaDevices : undefined);

  const stopTracks = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const clearTimers = useCallback(() => {
    if (tickRef.current) {
      clearInterval(tickRef.current);
      tickRef.current = null;
    }
    if (capRef.current) {
      clearTimeout(capRef.current);
      capRef.current = null;
    }
  }, []);

  const requestStream = useCallback(async () => {
    setErrorMessage(null);
    if (!devices || typeof devices.getUserMedia !== "function") {
      setStatus("error");
      setErrorMessage(
        "This browser does not support in-page recording. Please upload a file instead."
      );
      return null;
    }
    setStatus("requesting");
    try {
      const stream = await devices.getUserMedia(getConstraints(mode, cameraDeviceId));
      streamRef.current = stream;
      setStatus("ready");
      return stream;
    } catch (err) {
      // QA-105 — the whole taxonomy now lives in ./cameraDevices, asserted on
      // its own, and it adds the branch this recorder was missing: a device
      // another app holds exclusively rejects with NotReadableError /
      // TrackStartError. That used to fall into the generic "Please try again",
      // which is the least useful advice possible — retrying cannot succeed
      // until the other app releases the device.
      const { status: nextStatus, message } = cameraErrorFor(err, {
        subject: mode === "audio" ? "microphone" : "camera/microphone",
      });
      setStatus(nextStatus);
      setErrorMessage(message);
      return null;
    }
  }, [devices, mode, cameraDeviceId]);

  const start = useCallback(() => {
    const stream = streamRef.current;
    const R =
      Recorder ||
      (typeof MediaRecorder !== "undefined" ? MediaRecorder : undefined);
    if (!stream || !R) {
      setStatus("error");
      setErrorMessage("Recording is not available in this browser.");
      return;
    }
    chunksRef.current = [];
    setRecordedBlob(null);
    setElapsedMs(0);
    setRecordedBytes(0);

    const mimeType = pickSupportedMimeType(mode, R);
    let recorder;
    try {
      recorder = mimeType ? new R(stream, { mimeType }) : new R(stream);
    } catch (e) {
      // The requested mimeType was rejected; retry letting the UA pick. If even
      // the bare constructor throws (unsupported stream/codecs), fail to an
      // error state instead of throwing uncaught + leaving status on "recording".
      try {
        recorder = new R(stream);
      } catch (e2) {
        setStatus("error");
        setErrorMessage(
          "Could not start recording in this browser. Please try again or upload a file instead."
        );
        return;
      }
    }
    recorderRef.current = recorder;

    recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) {
        chunksRef.current.push(e.data);
        // Sum from the chunks themselves rather than recomputing over the array
        // every tick — the counter must stay cheap at one update per second for
        // the whole 5 minutes.
        setRecordedBytes((prev) => prev + e.data.size);
      }
    };
    recorder.onstop = () => {
      clearTimers();
      const type = mimeType ? mimeType.split(";")[0] : recorder.mimeType;
      const blob = new Blob(chunksRef.current, {
        type: type || (mode === "audio" ? "audio/webm" : "video/webm"),
      });
      // Size guard: a too-large blob must not proceed to upload. Surface via the
      // existing error mechanism (status/errorMessage) so the UI shows the
      // message and never offers the "Use" upload action for this recording.
      // Uses THIS mode's own cap (QA-95: audio's is far smaller than video's).
      if (isRecordingTooLarge(blob, maxBytes)) {
        setRecordedBlob(null);
        setStatus("error");
        setErrorMessage(
          `Recording is too large to upload (over ${Math.round(
            maxBytes / (1024 * 1024)
          )} MB). Please record a shorter clip or upload a file instead.`
        );
        return;
      }
      setRecordedBlob(blob);
      setStatus("recorded");
    };

    startedAtRef.current = Date.now();
    // Timeslice so chunks (and therefore the live byte counter) arrive during
    // the recording rather than all at once at stop().
    recorder.start(RECORDING_TIMESLICE_MS);
    setStatus("recording");

    tickRef.current = setInterval(() => {
      setElapsedMs(Date.now() - startedAtRef.current);
    }, 200);

    // Hard duration cap: auto-stop at the limit.
    capRef.current = setTimeout(() => {
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop();
      }
    }, maxDurationMs);
  }, [Recorder, clearTimers, maxBytes, maxDurationMs, mode]);

  const stop = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state === "recording") {
      recorder.stop();
    }
  }, []);

  // Discard the current recording and free the stream so the camera light goes
  // off. Returns to idle; the caller re-requests to record again.
  const reset = useCallback(() => {
    clearTimers();
    stopTracks();
    recorderRef.current = null;
    chunksRef.current = [];
    setRecordedBlob(null);
    setElapsedMs(0);
    setRecordedBytes(0);
    setStatus("idle");
    setErrorMessage(null);
  }, [clearTimers, stopTracks]);

  // Keep the stream alive for re-record, but go back to "ready" (preview) after
  // a recording so the user can record again without re-prompting.
  const rerecord = useCallback(() => {
    clearTimers();
    chunksRef.current = [];
    setRecordedBlob(null);
    setElapsedMs(0);
    setRecordedBytes(0);
    setStatus(streamRef.current ? "ready" : "idle");
  }, [clearTimers]);

  // Tear everything down on unmount so we never leak an open camera/mic.
  useEffect(() => {
    return () => {
      clearTimers();
      stopTracks();
    };
  }, [clearTimers, stopTracks]);

  return {
    status,
    errorMessage,
    recordedBlob,
    elapsedMs,
    recordedBytes,
    maxBytes,
    streamRef,
    requestStream,
    start,
    stop,
    reset,
    rerecord,
  };
}
